import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState} from '@/app/store';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setBearerTokenIfExists, setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBaseSplitBills} from './constants';
import {
  IConfirmPaymentRequest,
  ICreateSplitBillsLinkRequest,
  ICreateSplitBillsLinkResponse,
  IGetSplitDataRequest,
  IGetSplitDataResponse,
  IJoinLoggedUserToSplitRequest,
  IResendOtpRequest,
  ISaveParticipantPhoneRequest,
  ISaveParticipantPhoneResponse,
  IValidateOtpRequest,
} from './entities';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;
      setBearerTokenIfExists(headers, token);
      await setWafHeaders(baseUrl, headers);
      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBaseSplitBills.baseUrl;

  return reAuth(args, api, extraOptions, baseUrl, baseQuery);
};

export const splitsApi = createApi({
  reducerPath: EReducersPath.SPLITS_API,
  refetchOnReconnect: true,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    createSplitBillsLink: build.mutation<
      ICreateSplitBillsLinkResponse,
      ICreateSplitBillsLinkRequest
    >({
      query: body => ({
        url: apiBaseSplitBills.endpoints.createSplitBill,
        method: 'POST',
        body,
      }),
    }),
    getSplitData: build.query<IGetSplitDataResponse, IGetSplitDataRequest>({
      query: ({split_id}) => ({
        url: apiBaseSplitBills.endpoints.split.replace(':splitId', split_id),
        method: 'GET',
      }),
    }),
    saveParticipantPhone: build.mutation<
      ISaveParticipantPhoneResponse,
      ISaveParticipantPhoneRequest
    >({
      query: ({split_id, phone_number, participant_id, name}) => ({
        url: apiBaseSplitBills.endpoints.saveParticipantPhone.replace(
          ':splitId',
          split_id,
        ),
        method: 'POST',
        body: {phone_number, participant_id, name},
      }),
    }),
    validateParticipantOtp: build.mutation<void, IValidateOtpRequest>({
      query: ({split_id, participant_id, otp}) => ({
        url: apiBaseSplitBills.endpoints.validateParticipantOtp
          .replace(':splitId', split_id)
          .replace(':participantId', participant_id),
        method: 'POST',
        body: {otp},
      }),
    }),
    confirmPayment: build.mutation<void, IConfirmPaymentRequest>({
      query: ({split_id, participant_id}) => ({
        url: apiBaseSplitBills.endpoints.confirmPayment
          .replace(':splitId', split_id)
          .replace(':participantId', participant_id),
        method: 'POST',
      }),
    }),
    resendParticipantOtp: build.mutation<void, IResendOtpRequest>({
      query: ({split_id, participant_id}) => ({
        url: apiBaseSplitBills.endpoints.resendParticipantOtp
          .replace(':splitId', split_id)
          .replace(':participantId', participant_id),
        method: 'POST',
      }),
    }),
    joinLoggedUserToSplit: build.mutation<void, IJoinLoggedUserToSplitRequest>({
      query: ({split_id, participant_id}) => ({
        url: apiBaseSplitBills.endpoints.joinLoggedUserToSplit.replace(
          ':splitId',
          split_id,
        ),
        method: 'POST',
        body: {
          split_user_id: participant_id,
        },
      }),
    }),
  }),
});

export const {
  useCreateSplitBillsLinkMutation,
  useLazyGetSplitDataQuery,
  useSaveParticipantPhoneMutation,
  useValidateParticipantOtpMutation,
  useConfirmPaymentMutation,
  useResendParticipantOtpMutation,
  useJoinLoggedUserToSplitMutation,
} = splitsApi;
